import { useCallback, useEffect, useRef, useState } from 'react'
import type { DerivationInput } from '../crypto/spec'
import type { DeriveRequest, DeriveResponse } from '../crypto/worker-protocol'

export interface DerivationResult {
  password: string
  elapsedMs: number
}

export interface DerivationFailure {
  kind: 'memory' | 'wasm' | 'input' | 'unknown'
  message: string
}

export type DerivationStatus = 'idle' | 'working' | 'done' | 'error'

/**
 * Executa a derivação num Web Worker.
 *
 * O worker é criado sob demanda (na primeira geração) para não pagar o custo do
 * WASM em quem só veio ler a página, e recriado depois de um erro fatal.
 * Respostas de pedidos antigos são descartadas pelo id: sem isso, uma derivação
 * lenta poderia sobrescrever o resultado de uma mais recente.
 */
export function useDerivation() {
  const workerRef = useRef<Worker | null>(null)
  const requestId = useRef(0)
  const [status, setStatus] = useState<DerivationStatus>('idle')
  const [result, setResult] = useState<DerivationResult | null>(null)
  const [error, setError] = useState<DerivationFailure | null>(null)

  const disposeWorker = useCallback(() => {
    workerRef.current?.terminate()
    workerRef.current = null
  }, [])

  useEffect(() => disposeWorker, [disposeWorker])

  const ensureWorker = useCallback((): Worker => {
    if (workerRef.current) return workerRef.current
    const worker = new Worker(new URL('../crypto/worker.ts', import.meta.url), {
      type: 'module',
      name: 'dpgen-kdf',
    })
    worker.onmessage = (event: MessageEvent<DeriveResponse>) => {
      const response = event.data
      if (response.id !== requestId.current) return // resposta obsoleta
      if (response.ok) {
        setResult({ password: response.password, elapsedMs: response.elapsedMs })
        setError(null)
        setStatus('done')
      } else {
        setResult(null)
        setError({ kind: response.kind, message: response.message })
        setStatus('error')
      }
    }
    worker.onerror = (event) => {
      setResult(null)
      setError({ kind: 'wasm', message: event.message || 'worker error' })
      setStatus('error')
      // Um worker que estourou não é confiável; o próximo pedido cria outro.
      disposeWorker()
    }
    workerRef.current = worker
    return worker
  }, [disposeWorker])

  const derive = useCallback(
    (input: DerivationInput) => {
      requestId.current += 1
      setStatus('working')
      setError(null)
      setResult(null)
      const request: DeriveRequest = { id: requestId.current, input }
      ensureWorker().postMessage(request)
    },
    [ensureWorker],
  )

  /** Descarta o resultado e derruba o worker, levando a senha-mestra junto. */
  const reset = useCallback(() => {
    requestId.current += 1
    setStatus('idle')
    setResult(null)
    setError(null)
    disposeWorker()
  }, [disposeWorker])

  return { status, result, error, derive, reset }
}
